import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Box, Button, Stack, TextField } from '@mui/material'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import StrategicImperativeAudiencesField from './StrategicImperativeAudiencesField'

export type StrategicImperativeFormValues = {
    name: string
    audiences: string[]
}

type StrategicImperativeFormProps = {
    mode: 'Create' | 'Edit'
    defaultValues?: StrategicImperativeFormValues
    onSubmit: (values: StrategicImperativeFormValues) => Promise<unknown>
    onCancel: () => void
    onDelete?: () => void
}

const EMPTY_VALUES: StrategicImperativeFormValues = { name: '', audiences: [] }

const toSingleLine = (name: string) => name.replace(/\s*\n+\s*/g, ' ').trim()

export const StrategicImperativeForm = ({
    mode,
    defaultValues = EMPTY_VALUES,
    onSubmit,
    onCancel,
    onDelete,
}: StrategicImperativeFormProps) => {
    const { singular } = useStrategicImperativeDisplayName()
    const [submitError, setSubmitError] = useState<string | null>(null)
    const {
        control,
        handleSubmit,
        formState: { isSubmitting, isValid },
    } = useForm<StrategicImperativeFormValues>({ mode: 'onTouched', defaultValues })

    const submit = handleSubmit(async (values) => {
        setSubmitError(null)
        try {
            await onSubmit({ ...values, name: toSingleLine(values.name) })
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : `Failed to save this ${singular}`)
        }
    })

    return (
        <Stack component="form" noValidate onSubmit={submit} gap={2}>
            <Controller
                name="name"
                control={control}
                rules={{ validate: (name) => !!toSingleLine(name) || 'Title is required' }}
                render={({ field, fieldState }) => (
                    <TextField
                        label="Title"
                        required
                        multiline
                        minRows={2}
                        variant="filled"
                        fullWidth
                        autoComplete="off"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        inputRef={field.ref}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                    />
                )}
            />
            <Controller
                name="audiences"
                control={control}
                render={({ field }) => (
                    <StrategicImperativeAudiencesField value={field.value} onChange={field.onChange} />
                )}
            />
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                {onDelete && (
                    <Button variant="text" onClick={onDelete} disabled={isSubmitting} sx={{ minWidth: 110 }}>
                        Delete
                    </Button>
                )}
                <Button variant="outlined" onClick={onCancel} disabled={isSubmitting} sx={{ minWidth: 110 }}>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={!isValid || isSubmitting} sx={{ minWidth: 110 }}>
                    {mode === 'Edit' ? 'Save' : 'Create'}
                </Button>
            </Box>
        </Stack>
    )
}

export default StrategicImperativeForm
